import React, { useEffect, useRef, useState } from "react";
import { Loader2, Link2, X, Image as ImageIcon, Send, MessageCircle, Share2, Facebook, Linkedin, Copy, Sparkles, MoreVertical, Pencil, Trash2, Check, CornerDownRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { FileUpload } from "@/components/FileUpload";
import { toast } from "sonner";

export const REACTIONS = [
  { kind: "like", emoji: "👍", label: "Like" },
  { kind: "love", emoji: "❤️", label: "Love" },
  { kind: "happy", emoji: "😄", label: "Happy" },
  { kind: "congrats", emoji: "🎉", label: "Congrats" },
  { kind: "surprise", emoji: "😮", label: "Surprise" },
  { kind: "angry", emoji: "😠", label: "Angry" },
  { kind: "sad", emoji: "😢", label: "Sad" },
];
const EMOJI = Object.fromEntries(REACTIONS.map((r) => [r.kind, r.emoji]));
const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const initialsOf = (n) => (n || "M").trim().slice(0, 1).toUpperCase();

function Avatar({ name, photo, size = 36, testId }) {
  const px = `${size}px`;
  return (
    <span data-testid={testId} className="rounded-full bg-brand/15 text-brand grid place-items-center font-medium shrink-0 overflow-hidden"
      style={{ width: px, height: px, fontSize: Math.round(size * 0.4) }}>
      {photo ? <img src={photo} alt={name || ""} className="w-full h-full object-cover" loading="lazy" /> : initialsOf(name)}
    </span>
  );
}

function Composer({ onPosted }) {
  const { refreshUser } = useApp();
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [link, setLink] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const fetchPreview = async () => {
    const u = link.trim(); if (!u) return;
    setPreviewing(true);
    try { const { data } = await api.get("/link-preview", { params: { url: u } }); setPreview(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't preview that link."); }
    finally { setPreviewing(false); }
  };

  const aiWrite = async () => {
    if (aiPrompt.trim().length < 2) { toast.error("Share a few words about what to post."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/showcase/ai-draft", { prompt: aiPrompt.trim(), category: "personal" });
      setText([data.title, data.body].filter(Boolean).join("\n\n").trim() || data.body || "");
      toast.success(`ANXYA drafted your post ✨ (${data.cost} credit${data.cost === 1 ? "" : "s"} used)`);
      setAiOpen(false); setAiPrompt("");
      refreshUser?.();
    } catch (e) {
      if (e.response?.status === 402) window.dispatchEvent(new CustomEvent("open-recharge", { detail: { message: "Add credits to let ANXYA write your post" } }));
      else toast.error(apiError(e.response?.data?.detail));
    }
    finally { setAiBusy(false); }
  };

  const post = async () => {
    if (!text.trim() && images.length === 0 && !link.trim()) { toast.error("Write something, add a photo or a link."); return; }
    setBusy(true);
    try {
      await api.post("/showcase", {
        title: "", body: text.trim(), category: "personal", media_type: "image",
        images, link: link.trim(), link_preview: preview || {},
      });
      setText(""); setImages([]); setLink(""); setPreview(null); setShowLink(false);
      toast.success("Posted to Kind Social");
      onPosted?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rud-card rounded-2xl p-4" data-testid="kind-composer">
      <textarea data-testid="composer-text" value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder="Share something kind — what's on your mind?" className="rud-input w-full rounded-xl px-3 py-2.5 text-sm resize-none" />

      {aiOpen && (
        <div className="mt-2 rounded-xl border border-brand/30 bg-brand/5 p-3" data-testid="composer-ai-row">
          <div className="text-[11px] uppercase tracking-widest text-brand flex items-center gap-1.5 mb-2"><Sparkles size={13} /> Let ANXYA write it — share your thoughts</div>
          <textarea data-testid="composer-ai-prompt" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={2}
            placeholder="e.g. A grateful post about finishing my first 5K run this morning" className="rud-input w-full rounded-lg px-3 py-2 text-sm resize-none" />
          <div className="flex items-center gap-2 mt-2">
            <button data-testid="composer-ai-generate" onClick={aiWrite} disabled={aiBusy || aiPrompt.trim().length < 2}
              className="btn-primary rounded-full h-8 px-4 text-xs flex items-center gap-1.5 disabled:opacity-60">
              {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Write my post
            </button>
            <span className="text-[11px] text-sec">Uses credits · you can edit before posting</span>
            <button data-testid="composer-ai-cancel" onClick={() => { setAiOpen(false); setAiPrompt(""); }} className="ml-auto text-sec hover:text-brand"><X size={15} /></button>
          </div>
        </div>
      )}

      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid="composer-images">
          {images.map((src, i) => (
            <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-rud">
              <img src={src} alt="" className="w-full h-full object-cover" />
              <button onClick={() => setImages((a) => a.filter((_, j) => j !== i))} data-testid={`composer-image-remove-${i}`}
                className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {showLink && (
        <div className="mt-2 flex gap-2" data-testid="composer-link-row">
          <input data-testid="composer-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste a link (https://…)" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="composer-link-preview" onClick={fetchPreview} disabled={previewing || !link.trim()} className="btn-ghost rounded-lg px-3 text-xs border border-rud disabled:opacity-50">{previewing ? <Loader2 size={14} className="animate-spin" /> : "Preview"}</button>
        </div>
      )}
      {preview && (
        <div className="mt-2 rounded-xl border border-rud overflow-hidden flex" data-testid="composer-link-preview-card">
          {preview.image && <img src={preview.image} alt="" className="w-24 h-24 object-cover shrink-0" />}
          <div className="p-2.5 min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-sec">{preview.domain}</div>
            <div className="text-sm font-medium truncate">{preview.title}</div>
            <div className="text-[11px] text-sec line-clamp-2">{preview.description}</div>
          </div>
          <button onClick={() => { setPreview(null); setLink(""); }} className="p-2 self-start text-sec"><X size={14} /></button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <FileUpload category="post" accept="image/*" label={<span className="flex items-center gap-1.5"><ImageIcon size={15} /> Photo</span>} testId="composer-add-photo"
          onUploaded={(file) => setImages((a) => [...a, file.absoluteUrl].slice(0, 6))} />
        <button data-testid="composer-toggle-link" onClick={() => setShowLink((v) => !v)} className="btn-ghost rounded-lg h-9 px-3 text-sm flex items-center gap-1.5 border border-rud"><Link2 size={15} /> Link</button>
        <button data-testid="composer-ai-toggle" onClick={() => setAiOpen((v) => !v)} className={`rounded-lg h-9 px-3 text-sm flex items-center gap-1.5 border transition-colors ${aiOpen ? "border-brand text-brand bg-brand/10" : "border-rud text-sec hover:text-brand hover:border-brand/50"}`}><Sparkles size={15} /> Write with AI</button>
        <button data-testid="composer-post" onClick={post} disabled={busy} className="btn-primary rounded-full h-9 px-5 text-sm ml-auto flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Post
        </button>
      </div>
    </div>
  );
}

function ReactionBar({ post, onReact }) {
  const [open, setOpen] = useState(false);
  const counts = post.reaction_counts || {};
  const total = post.total_reactions || 0;
  const mine = post.my_reaction;
  const top = Object.entries(counts).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k]) => EMOJI[k]);
  return (
    <div className="flex items-center gap-3 mt-3 relative">
      <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
        <button data-testid={`react-btn-${post.id}`} onClick={() => onReact(post, mine || "like")}
          className={`text-sm rounded-full px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${mine ? "border-brand/50 bg-brand/10 text-brand" : "border-rud text-sec hover:text-[var(--rud-text)]"}`}>
          <span>{mine ? EMOJI[mine] : "👍"}</span> {mine ? REACTIONS.find((r) => r.kind === mine)?.label : "React"}
        </button>
        {open && (
          <div className="absolute bottom-full left-0 pb-2 z-30">
            <div data-testid={`react-picker-${post.id}`} className="flex gap-1 rud-card rounded-full px-2 py-1.5 shadow-xl">
              {REACTIONS.map((r) => (
                <button key={r.kind} data-testid={`react-${r.kind}-${post.id}`} title={r.label} onClick={() => { onReact(post, r.kind); setOpen(false); }}
                  className="text-xl leading-none px-0.5 hover:scale-125 transition-transform">{r.emoji}</button>
              ))}
            </div>
          </div>
        )}
      </div>
      {total > 0 && <span className="text-xs text-sec flex items-center gap-1" data-testid={`react-count-${post.id}`}>{top.join("")} {total}</span>}
    </div>
  );
}

function ShareRow({ post }) {
  const [open, setOpen] = useState(false);
  const text = `${post.title ? post.title + " — " : ""}${(post.body || "").slice(0, 140)}`.trim() || "Something kind on My Kind";
  const url = (post.link && post.link.trim()) ? post.link.trim() : (typeof window !== "undefined" ? window.location.origin : "");
  const et = encodeURIComponent(text), eu = encodeURIComponent(url);
  const links = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${eu}`,
    telegram: `https://t.me/share/url?url=${eu}&text=${et}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${eu}`,
  };
  const openShare = (href) => window.open(href, "_blank", "noopener,noreferrer,width=680,height=640");
  const wechat = () => openShare(`https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${eu}`);
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Couldn't copy"); } };
  const Btn = ({ testid, onClick, icon: Icon, color, label }) => (
    <button data-testid={testid} onClick={onClick} title={label}
      className="w-8 h-8 grid place-items-center rounded-full border border-rud hover:border-brand/60 transition-colors" style={color ? { color } : undefined}>
      <Icon size={15} />
    </button>
  );
  return (
    <div className="relative">
      <button data-testid={`share-toggle-${post.id}`} onClick={() => setOpen((v) => !v)}
        className="text-sm rounded-full px-3 py-1.5 border border-rud text-sec hover:text-[var(--rud-text)] transition-colors flex items-center gap-1.5">
        <Share2 size={14} /> Share
      </button>
      {open && (
        <div data-testid={`share-menu-${post.id}`} className="absolute bottom-full right-0 mb-1 flex items-center gap-1.5 rud-card rounded-full px-2.5 py-2 shadow-xl z-20">
          <Btn testid={`share-whatsapp-${post.id}`} onClick={() => openShare(links.whatsapp)} icon={MessageCircle} color="#25D366" label="WhatsApp" />
          <Btn testid={`share-facebook-${post.id}`} onClick={() => openShare(links.facebook)} icon={Facebook} color="#1877F2" label="Facebook" />
          <Btn testid={`share-telegram-${post.id}`} onClick={() => openShare(links.telegram)} icon={Send} color="#26A5E4" label="Telegram" />
          <Btn testid={`share-linkedin-${post.id}`} onClick={() => openShare(links.linkedin)} icon={Linkedin} color="#0A66C2" label="LinkedIn" />
          <Btn testid={`share-wechat-${post.id}`} onClick={wechat} icon={MessageCircle} color="#07C160" label="WeChat (QR)" />
          <Btn testid={`share-copy-${post.id}`} onClick={copy} icon={Copy} label="Copy link" />
        </div>
      )}
    </div>
  );
}

function Comments({ post, currentUser }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [count, setCount] = useState(post.comment_count || 0);
  const myPhoto = currentUser?.social_profile?.photo || currentUser?.picture || "";

  const load = async () => {
    try {
      const { data } = await api.get(`/showcase/${post.id}/comments`);
      setList(data.comments || []);
      setCount((data.comments || []).length);
    } catch { /* */ } finally { setLoaded(true); }
  };
  const toggle = () => { const n = !open; setOpen(n); if (n && !loaded) load(); };

  const submit = async (parentId = "") => {
    const body = (parentId ? replyText : text).trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/showcase/${post.id}/comment`, { body, parent_id: parentId });
      if (parentId) { setReplyText(""); setReplyTo(null); } else setText("");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const top = list.filter((c) => !c.parent_id);
  const repliesOf = (id) => list.filter((c) => c.parent_id === id);

  return (
    <div className="mt-3 border-t border-rud pt-3">
      <button data-testid={`comments-toggle-${post.id}`} onClick={toggle}
        className="text-sm rounded-full px-3 py-1.5 border border-rud text-sec hover:text-[var(--rud-text)] transition-colors flex items-center gap-1.5">
        <MessageCircle size={14} /> {count > 0 ? `${count} comment${count === 1 ? "" : "s"}` : "Comment"}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`comments-${post.id}`}>
          {currentUser && (
            <div className="flex items-center gap-2">
              <Avatar name={currentUser.name} photo={myPhoto} size={32} />
              <input data-testid={`comment-input-${post.id}`} value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                placeholder="Write a kind comment…" className="rud-input flex-1 rounded-full px-3 py-2 text-sm" />
              <button data-testid={`comment-send-${post.id}`} onClick={() => submit()} disabled={busy || !text.trim()}
                className="btn-primary rounded-full h-9 w-9 grid place-items-center disabled:opacity-50">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </div>
          )}

          {loaded && top.length === 0 && <p className="text-xs text-sec pl-1" data-testid={`comments-empty-${post.id}`}>No comments yet — start the conversation.</p>}

          {top.map((c) => (
            <div key={c.id} data-testid={`comment-${c.id}`}>
              <div className="flex items-start gap-2">
                <Avatar name={c.author_name} photo={c.author_photo} size={32} />
                <div className="flex-1 min-w-0">
                  <div className="rud-card rounded-2xl px-3 py-2">
                    <div className="text-xs font-medium truncate">{c.author_name}</div>
                    <div className="text-sm whitespace-pre-wrap break-words mt-0.5">{c.body}</div>
                  </div>
                  <div className="flex items-center gap-3 mt-1 pl-1 text-[11px] text-sec">
                    <span>{fmt(c.created_at)}</span>
                    {currentUser && (
                      <button data-testid={`reply-btn-${c.id}`} onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyText(""); }}
                        className="hover:text-brand flex items-center gap-1"><CornerDownRight size={12} /> Reply</button>
                    )}
                  </div>

                  {replyTo === c.id && currentUser && (
                    <div className="flex items-center gap-2 mt-2">
                      <Avatar name={currentUser.name} photo={myPhoto} size={26} />
                      <input autoFocus data-testid={`reply-input-${c.id}`} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(c.id); } }}
                        placeholder={`Reply to ${c.author_name}…`} className="rud-input flex-1 rounded-full px-3 py-1.5 text-sm" />
                      <button data-testid={`reply-send-${c.id}`} onClick={() => submit(c.id)} disabled={busy || !replyText.trim()}
                        className="btn-primary rounded-full h-8 w-8 grid place-items-center disabled:opacity-50"><Send size={13} /></button>
                    </div>
                  )}

                  {repliesOf(c.id).map((r) => (
                    <div key={r.id} data-testid={`comment-${r.id}`} className="flex items-start gap-2 mt-2 ml-3">
                      <Avatar name={r.author_name} photo={r.author_photo} size={26} />
                      <div className="flex-1 min-w-0">
                        <div className="rud-card rounded-2xl px-3 py-2">
                          <div className="text-xs font-medium truncate">{r.author_name}</div>
                          <div className="text-sm whitespace-pre-wrap break-words mt-0.5">{r.body}</div>
                        </div>
                        <div className="text-[11px] text-sec mt-1 pl-1">{fmt(r.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PostCard({ post, onReact, currentUserId, currentUser, onDelete, onSaved }) {
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.body || "");
  const [saving, setSaving] = useState(false);
  const isOwner = currentUserId && post.user_id === currentUserId;

  const save = async () => {
    if (!editText.trim() && !(post.images?.length) && !post.link) { toast.error("A post can't be empty."); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/showcase/${post.id}`, {
        title: post.title || "", body: editText.trim(), link: post.link || "",
        images: post.images || [], link_preview: post.link_preview || {},
      });
      onSaved?.(data.post);
      setEditing(false);
      toast.success("Post updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async () => {
    setMenu(false);
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    try { await api.delete(`/showcase/${post.id}`); onDelete?.(post.id); toast.success("Post deleted"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rud-card rounded-2xl p-4" data-testid={`post-${post.id}`}>
      <div className="flex items-center gap-2.5">
        <Avatar name={post.author_name} photo={post.author_photo} size={36} testId={`post-avatar-${post.id}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{post.author_name}</div>
          <div className="text-[11px] text-sec">{fmt(post.created_at)}{post.edited_at ? " · edited" : ""}</div>
        </div>
        {isOwner && !editing && (
          <div className="relative" onMouseLeave={() => setMenu(false)}>
            <button data-testid={`post-menu-${post.id}`} onClick={() => setMenu((m) => !m)} className="text-sec hover:text-brand p-1 rounded-full"><MoreVertical size={16} /></button>
            {menu && (
              <div className="absolute right-0 mt-1 z-30 rud-card rounded-xl p-1 min-w-[130px] shadow-xl">
                <button data-testid={`post-edit-${post.id}`} onClick={() => { setEditing(true); setEditText(post.body || ""); setMenu(false); }}
                  className="w-full text-left px-3 py-1.5 text-xs rounded-lg hover:bg-white/10 flex items-center gap-2"><Pencil size={13} /> Edit</button>
                <button data-testid={`post-delete-${post.id}`} onClick={del}
                  className="w-full text-left px-3 py-1.5 text-xs rounded-lg hover:bg-white/10 text-rose-400 flex items-center gap-2"><Trash2 size={13} /> Delete</button>
              </div>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <div className="mt-2" data-testid={`post-edit-form-${post.id}`}>
          <textarea data-testid={`post-edit-text-${post.id}`} value={editText} onChange={(e) => setEditText(e.target.value)} rows={3}
            className="rud-input w-full rounded-xl px-3 py-2.5 text-sm resize-none" />
          <div className="flex items-center gap-2 mt-2">
            <button data-testid={`post-edit-save-${post.id}`} onClick={save} disabled={saving} className="btn-primary rounded-full h-8 px-4 text-xs flex items-center gap-1.5 disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save
            </button>
            <button data-testid={`post-edit-cancel-${post.id}`} onClick={() => setEditing(false)} className="btn-ghost rounded-full h-8 px-3 text-xs border border-rud">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          {post.title && <div className="font-serif text-lg mt-2">{post.title}</div>}
          {post.body && <p className="text-sm mt-2 whitespace-pre-wrap leading-relaxed">{post.body}</p>}
        </>
      )}

      {(post.images?.length ? post.images : (post.media_url ? [post.media_url] : [])).length > 0 && (
        <div className={`grid gap-1.5 mt-3 ${(post.images?.length || 1) > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
          {(post.images?.length ? post.images : [post.media_url]).slice(0, 4).map((src, i) => (
            <img key={i} src={src} alt="" className="w-full rounded-lg object-cover max-h-72" data-testid={`post-image-${post.id}-${i}`} />
          ))}
        </div>
      )}

      {post.link_preview?.title && (
        <a href={post.link} target="_blank" rel="noopener noreferrer" data-testid={`post-link-${post.id}`}
          className="mt-3 rounded-xl border border-rud overflow-hidden flex hover:border-brand/50 transition-colors">
          {post.link_preview.image && <img src={post.link_preview.image} alt="" className="w-24 h-24 object-cover shrink-0" />}
          <div className="p-2.5 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-sec">{post.link_preview.domain}</div>
            <div className="text-sm font-medium truncate">{post.link_preview.title}</div>
            <div className="text-[11px] text-sec line-clamp-2">{post.link_preview.description}</div>
          </div>
        </a>
      )}

      <div className="flex items-center justify-between gap-3">
        <ReactionBar post={post} onReact={onReact} />
        <ShareRow post={post} />
      </div>

      <Comments post={post} currentUser={currentUser} />
    </div>
  );
}

export default function KindFeed() {
  const { user } = useApp();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/showcase", { params: { category: "personal" } }); setPosts(data.posts || []); }
    catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const react = async (post, kind) => {
    // optimistic
    setPosts((ps) => ps.map((p) => {
      if (p.id !== post.id) return p;
      const counts = { ...(p.reaction_counts || {}) };
      const prev = p.my_reaction;
      if (prev) { counts[prev] = (counts[prev] || 1) - 1; if (counts[prev] <= 0) delete counts[prev]; }
      let my = kind;
      if (prev === kind) { my = null; } else { counts[kind] = (counts[kind] || 0) + 1; }
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      return { ...p, reaction_counts: counts, total_reactions: total, my_reaction: my };
    }));
    try {
      const { data } = await api.post(`/showcase/${post.id}/react`, {}, { params: { kind } });
      // sync to authoritative server counts (no stale zero-count reactions)
      setPosts((ps) => ps.map((p) => p.id === post.id
        ? { ...p, reaction_counts: data.reaction_counts || {}, total_reactions: data.total_reactions || 0, my_reaction: data.my_reaction }
        : p));
    }
    catch { load(); }
  };

  const onDeleted = (id) => setPosts((ps) => ps.filter((p) => p.id !== id));
  const onSaved = (updated) => setPosts((ps) => ps.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));

  return (
    <div className="space-y-4" data-testid="kind-feed">
      {user && <Composer onPosted={load} />}
      {loading ? (
        <div className="rud-card rounded-2xl p-8 text-center text-sec text-sm">Loading the kind feed…</div>
      ) : posts.length === 0 ? (
        <div className="rud-card rounded-2xl p-8 text-center text-sec text-sm" data-testid="kind-feed-empty">No posts yet — be the first to share something kind ✨</div>
      ) : (
        posts.map((p) => <PostCard key={p.id} post={p} onReact={react} currentUserId={user?.user_id} currentUser={user} onDelete={onDeleted} onSaved={onSaved} />)
      )}
    </div>
  );
}
